import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PortfolioProvider, usePortfolio } from "./context/PortfolioContext";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import MyHoldings from "./pages/MyHoldings";
import Members from "./pages/Members";
import MemberDetail from "./pages/MemberDetail";
import NavHistory from "./pages/NavHistory";
import Cashflows from "./pages/Cashflows";
import Bonus from "./pages/Bonus";
import Portfolio from "./pages/Portfolio";
import ProfitLoss from "./pages/ProfitLoss";
import Ledger from "./pages/Ledger";
import AdminLayout from "./pages/admin/AdminLayout";
import AddTransaction from "./pages/admin/AddTransaction";
import ManageMembers from "./pages/admin/ManageMembers";
import PortfolioValues from "./pages/admin/PortfolioValues";
import PnlTabs from "./pages/admin/PnlTabs";
import AuditLog from "./pages/admin/AuditLog";
import PageAccess from "./pages/admin/PageAccess";

// ✅ Backend URL - uses env var (REACT_APP_BACKEND_URL)
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

function Private({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return <PortfolioProvider>{children}</PortfolioProvider>;
}

function GatedPage({ gate, children }) {
  const { user } = useAuth();
  const { settings, loading } = usePortfolio() || {};
  if (loading) return null;
  const visibility = settings?.[gate] || "all";
  if (visibility === "admin" && user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();

  // ✅ Backend connection test
  useEffect(() => {
    console.log("🔥 AppRouter loaded");

    fetch(`${BASE_URL}/api/`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log("✅ Backend Connected:", data);
      })
      .catch((err) => {
        console.error("❌ Backend Error:", err);
      });
  }, []);

  // OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/" element={<Private><Dashboard /></Private>} />
      <Route path="/my-holdings" element={<Private><MyHoldings /></Private>} />
      <Route
        path="/members"
        element={
          <Private>
            <GatedPage gate="membersPageVisibility">
              <Members />
            </GatedPage>
          </Private>
        }
      />
      <Route
        path="/members/:id"
        element={
          <Private>
            <GatedPage gate="membersPageVisibility">
              <MemberDetail />
            </GatedPage>
          </Private>
        }
      />
      <Route path="/nav-history" element={<Private><NavHistory /></Private>} />
      <Route path="/cashflows" element={<Private><Cashflows /></Private>} />
      <Route path="/bonus" element={<Private><Bonus /></Private>} />
      <Route
        path="/portfolio"
        element={
          <Private>
            <GatedPage gate="portfolioPageVisibility">
              <Portfolio />
            </GatedPage>
          </Private>
        }
      />
      <Route path="/profit-loss" element={<Private><ProfitLoss /></Private>} />
      <Route
        path="/ledger"
        element={
          <Private>
            <GatedPage gate="ledgerPageVisibility">
              <Ledger />
            </GatedPage>
          </Private>
        }
      />

      <Route path="/admin" element={<Private><AdminLayout /></Private>}>
        <Route index element={<Navigate to="/admin/add-transaction" replace />} />
        <Route path="add-transaction" element={<AddTransaction />} />
        <Route path="members" element={<ManageMembers />} />
        <Route path="portfolio-values" element={<PortfolioValues />} />
        <Route path="profit-loss" element={<PnlTabs />} />
        <Route path="page-access" element={<PageAccess />} />
        <Route path="audit-log" element={<AuditLog />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}
